import React from "react";
import * as Sentry from "@sentry/react";
import RocketErrorFallback from "components/RocketErrorFallback";
import {
  isChunkLoadError,
  reloadWithCacheBust,
  tryReloadForStaleChunk,
} from "utils/chunkReload";

export { isChunkLoadError };

class LazyRouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, reloading: false };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Lazy route failed to load", error, errorInfo);

    if (!isChunkLoadError(error)) {
      // Real app bugs (ReferenceError, TypeError, etc.) must always reach Sentry.
      // Previously we only reported exhausted chunk-reload loops, which hid
      // production crashes behind the rocket "Refresh Page" screen.
      Sentry.captureException(error, {
        tags: { lazy_route_runtime_error: "true" },
        contexts: {
          react: {
            componentStack: errorInfo?.componentStack,
          },
        },
      });
      return;
    }

    if (tryReloadForStaleChunk() === "reloaded") {
      this.setState({ reloading: true });
      return;
    }

    // Auto-retries exhausted; not a one-shot stale deploy.
    Sentry.captureException(error, {
      tags: { chunk_reload_loop: "true" },
    });
  }

  handleRetry = () => {
    this.setState({ error: null });
    reloadWithCacheBust();
  };

  render() {
    const { error, reloading } = this.state;
    const { children } = this.props;

    if (!error) {
      return children;
    }

    if (reloading) {
      return null;
    }

    const isChunkError = isChunkLoadError(error);

    return (
      <RocketErrorFallback
        supportingText={
          isChunkError
            ? "Your connection may have dropped while loading part of the app. Refresh to try again."
            : "We encountered a gravitational anomaly while loading this page."
        }
        onRetry={this.handleRetry}
      />
    );
  }
}

export default LazyRouteErrorBoundary;
